import React from 'react';

interface StatusCount {
  status: string;
  label: string;
  icon: string;
  count: number;
}

interface ClientSummaryRowProps {
  totalClients: number;
  statusCounts: StatusCount[];
}

/**
 * ClientSummaryRow Component
 * Phase 4: Practice-wide client summary statistics
 *
 * Displays:
 * - Total client count
 * - Count per status tier (with badge icons)
 * - Always shows full practice stats (doesn't change with filter)
 *
 * Format: 📊 11 Clients | 🟢 5 Excelling | 🟡 2 Need Attention | 🟠 1 At Risk
 */
export const ClientSummaryRow: React.FC<ClientSummaryRowProps> = ({
  totalClients,
  statusCounts
}) => {
  // Filter to only show statuses with counts > 0
  const activeStatuses = statusCounts.filter(s => s.count > 0);

  return (
    <div className="client-summary-row">
      {/* Total Count */}
      <span className="summary-item total">
        <span className="summary-icon">📊</span>
        <span className="summary-text">
          {totalClients} {totalClients === 1 ? 'Client' : 'Clients'}
        </span>
      </span>

      {/* Status Counts (only show if count > 0) */}
      {activeStatuses.map((status, _index) => (
        <React.Fragment key={status.status}>
          <span className="summary-divider">|</span>
          <span className="summary-item">
            <span className="summary-icon">{status.icon}</span>
            <span className="summary-count">{status.count}</span>
            <span className="summary-label">{status.label}</span>
          </span>
        </React.Fragment>
      ))}

      {/* Empty state if no clients */}
      {totalClients === 0 && (
        <span className="summary-empty">No clients to display</span>
      )}

      <style>{`
        .client-summary-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 20px;
          background: var(--bg-secondary);
          border-bottom: 1px solid var(--border);
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary);
          flex-wrap: wrap;
        }

        .summary-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }

        .summary-item.total {
          font-weight: 600;
          color: var(--text-primary);
        }

        .summary-icon {
          font-size: 14px;
          line-height: 1;
        }

        .summary-text {
          font-size: 13px;
          line-height: 1;
        }

        .summary-count {
          font-weight: 600;
          font-size: 13px;
          line-height: 1;
        }

        .summary-label {
          font-size: 13px;
          color: var(--text-secondary);
          line-height: 1;
        }

        .summary-divider {
          color: var(--border-primary);
          font-weight: 300;
          margin: 0 2px;
        }

        .summary-empty {
          color: var(--text-muted);
          font-style: italic;
        }

        /* Responsive: adjust layout on smaller screens */
        @media (max-width: 768px) {
          .client-summary-row {
            font-size: 12px;
            gap: 8px;
          }

          .summary-item {
            gap: 4px;
          }
        }
      `}</style>
    </div>
  );
};
